import { type JSX } from 'react';
import { cn } from '../lib/utils.ts';

/**
 * HapMetrix brand mark - the rounded-square "metrics bars" icon, traced exactly
 * from brand-assets/svg/hapmetrix-icon.svg. Brand colours are fixed (a logo does
 * not re-tint with the theme): violet tile (text-primary), lavender + white bars.
 */
export function BrandMark({ className, title }: { className?: string; title?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 84 84"
      className={cn('text-primary', className)}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {title ? <title>{title}</title> : null}
      <rect width="84" height="84" rx="21" fill="currentColor" />
      <rect x="18.5" y="39" width="11" height="25" rx="4" fill="#CDBCFF" />
      <rect x="36.5" y="20" width="11" height="44" rx="4" fill="#FFFFFF" />
      <rect x="54.5" y="30" width="11" height="34" rx="4" fill="#FFFFFF" />
    </svg>
  );
}

/**
 * Full lockup: brand mark + "HapMetrix" wordmark. "Hap" in the ink foreground,
 * "Metrix" in the brand violet - matching brand-assets/svg/hapmetrix-lockup.svg,
 * but rendered in the app font so it stays crisp at every size and theme.
 */
export function BrandLogo({
  className,
  wordmarkClassName,
}: {
  className?: string;
  /** Lets a host hide the wordmark at narrow widths (the header goes mark-only
   *  below sm so the top bar stays one row at 375px). */
  wordmarkClassName?: string;
}): JSX.Element {
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <BrandMark className="h-7 w-7 shrink-0" />
      <span className={cn('text-lg font-bold tracking-tight', wordmarkClassName)}>
        <span className="text-foreground">Hap</span>
        <span className="text-primary">Metrix</span>
      </span>
    </span>
  );
}
